import { EventChangeArg } from '@fullcalendar/core';
import { DropArg, EventReceiveArg } from '@fullcalendar/interaction';
import {
  Goal,
  LifeArea,
  SmartList,
  Tag,
  Task,
  TaskActionType,
  TaskType,
  Timeslot,
  Timestamp,
} from '@griply/types';
import {
  createPlannedOccurrenceForDate,
  getCalendarTaskDuration,
  getCalendarTaskStartTime,
  getTaskRescheduleUpdate,
  getWeekStartsOn,
  HALF_HOUR,
  isGoalDeadlineEvent,
} from '@griply/utils';
import { endOfDay, isSameDay } from 'date-fns';
import { useUpdateGoal } from 'features/goals';
import { useUpdateTask } from 'features/tasks';
import { useCallback } from 'react';
import { useDragExternalCalendarEvent } from 'shared/contexts/event-form';
import { useToday } from 'shared/contexts/today';
import { useUser } from 'shared/hooks/use-user';
import {
  buildExternalEventDragRequest,
  ExtendedProps,
  getCalendarEventDate,
} from 'shared/lib/full-calendar';
import { getErrorReporter } from 'shared/services/error-tracking';
import { DataType } from 'shared/types/data-type';

import { useCalendarSlotMenu } from './use-calendar-slot-menu';
import { useCreateTimeslotTask } from './use-create-timeslot-task';

const createTimeslotFromCalendarEvent = (
  arg: EventChangeArg | DropArg,
  externalDurationMs?: number | null,
): Timeslot | null => {
  const isDropArg = !('event' in arg);
  const fcEvent = isDropArg ? null : arg.event;

  const allDay = isDropArg ? arg.allDay : fcEvent!.allDay;

  if (allDay) {
    return null;
  }

  const start = isDropArg ? arg.date : fcEvent!.start;
  const end = isDropArg ? null : fcEvent!.end;

  if (!start) {
    return null; // Should not happen for non-allDay events
  }

  const transitionedFromAllDay =
    !isDropArg && (arg as EventChangeArg).oldEvent.allDay && !fcEvent!.allDay;

  const fallbackDuration =
    fcEvent?.extendedProps?.duration || externalDurationMs || HALF_HOUR;

  const durationMs =
    end && !transitionedFromAllDay
      ? getCalendarTaskDuration(start, end)
      : fallbackDuration;

  return {
    startTime: getCalendarTaskStartTime(start),
    duration: durationMs,
  };
};

type HandleTaskUpdateOptions = {
  isDeadline?: boolean;
  occurrenceDate?: Timestamp;
  originDate?: Timestamp;
};

export const useCalendarEventControls = (
  lifeAreas: LifeArea[],
  goals: Goal[],
  tasks: Task[],
  tags: Tag[],
  smartLists: SmartList[],
) => {
  const user = useUser();
  const weekStartsOn = getWeekStartsOn(user);
  const today = useToday();

  const { submit: updateTask } = useUpdateTask();
  const { submit: updateGoal } = useUpdateGoal();
  const createTimeslotTask = useCreateTimeslotTask();
  const dragExternalEvent = useDragExternalCalendarEvent();

  const { onSelect, onDateTime, slotContextMenu } = useCalendarSlotMenu(
    lifeAreas,
    goals,
    tags,
    smartLists,
  );

  const handleGoalUpdate = useCallback(
    (goal: Goal, date: Timestamp) => {
      // All-day deadline drop: FullCalendar's `date` is UTC midnight — store the
      // local calendar day it was dropped on, not the previous day in behind-UTC zones.
      updateGoal({
        id: goal.id,
        deadline: endOfDay(new Date(getCalendarEventDate(date, true)!)),
      });
    },
    [updateGoal],
  );

  const handleTaskUpdate = useCallback(
    (
      task: Task,
      date: Timestamp,
      timeslot: Timeslot | null,
      { isDeadline, occurrenceDate, originDate }: HandleTaskUpdateOptions = {},
    ) => {
      // An all-day drop arrives as UTC midnight, so store the local day it landed on.
      const rescheduleDate = timeslot
        ? date
        : new Date(getCalendarEventDate(date, true)!);

      // A placed occurrence relocates to any day; an inferred due date only re-times its own.
      const originalDate =
        occurrenceDate ??
        (originDate && isSameDay(originDate, rescheduleDate)
          ? originDate
          : undefined);

      updateTask(
        getTaskRescheduleUpdate(task, rescheduleDate, timeslot, {
          today,
          weekStartsOn,
          isDeadline,
          originalDate,
        }),
      );
    },
    [today, updateTask, weekStartsOn],
  );

  const onEventChange = useCallback(
    (changeEvent: EventChangeArg) => {
      let extendedProps: EventChangeArg['event']['extendedProps'];
      let start: EventChangeArg['event']['start'];
      let end: EventChangeArg['event']['end'];
      let allDay: EventChangeArg['event']['allDay'];
      try {
        ({ extendedProps, start, end, allDay } = changeEvent.event);
      } catch {
        // Stale FullCalendar EventImpl — `_def` is undefined, getters throw.
        return;
      }
      const { start: oldStart, allDay: oldAllDay } = changeEvent.oldEvent;

      if (!start) {
        return;
      }

      // An external event's id is the provider's own and never changes with its dates, so
      // (unlike occurrences and deadline markers) it must NOT be reverted — the optimistic
      // cache patch keeps it at the drop position until the write lands.
      if (extendedProps.externalId) {
        const request = buildExternalEventDragRequest({
          extendedProps,
          start,
          end,
          allDay,
        });
        request && dragExternalEvent(request, () => changeEvent.revert());
        return;
      }

      // A task merely linked to a life area carries a taskId — only bare life-area events land here.
      if (extendedProps.lifeAreaId && !extendedProps.taskId) {
        const lifeArea = lifeAreas.find(
          ({ id }) => id === extendedProps.lifeAreaId,
        );
        if (!lifeArea) {
          return;
        }

        // If the life-area is dragged outside allDay, create a new task
        createTimeslotTask(
          {
            id: lifeArea.id,
            name: lifeArea.name,
            actionType: TaskActionType.LifeAreaTimeslot,
          },
          start,
          createTimeslotFromCalendarEvent(changeEvent),
        );
        return;
      }

      if (
        isGoalDeadlineEvent({
          goalId: extendedProps.goalId,
          taskId: extendedProps.taskId,
        })
      ) {
        const goal = goals.find(({ id }) => id === extendedProps.goalId);
        if (!goal) {
          return;
        }

        // If the goal is moved within allDay, update its deadline
        if (allDay) {
          handleGoalUpdate(goal, start);
          return;
        }

        // If the goal is dragged outside allDay, create a new task
        createTimeslotTask(
          {
            id: goal.id,
            name: goal.name,
            actionType: TaskActionType.GoalTimeslot,
          },
          start,
          createTimeslotFromCalendarEvent(changeEvent),
        );
        return;
      }

      const task = tasks.find(({ id }) => id === extendedProps.taskId);
      if (!task) {
        return;
      }

      // repeating and habit tasks get a planned occurrence when the user moves a slot
      if (task.type !== TaskType.Task) {
        const timeslot = createTimeslotFromCalendarEvent(changeEvent);

        // FullCalendar reports all-day starts as UTC midnight; store (and match the
        // original occurrence on) the LOCAL dropped day so the occurrence keeps the
        // timezone it was created in — never a day earlier in behind-UTC zones.
        const schedules = createPlannedOccurrenceForDate(
          task.schedules,
          new Date(getCalendarEventDate(start, allDay)!),
          {
            timeslot,
            weekStartsOn,
            today,
            originalDate: oldStart
              ? new Date(getCalendarEventDate(oldStart, oldAllDay)!)
              : undefined,
            id: extendedProps.occurrenceId,
          },
        );

        // An occurrence's event id encodes its date + duration, so this change mints a new
        // id. Drop the transient — still keyed on the old id — or it never reconciles with
        // the re-rendered event and the occurrence vanishes until reload. Read the event
        // before reverting: `revert()` detaches the EventApi.
        changeEvent.revert();

        // update the schedule with a `timeslot` based on the event
        updateTask({ id: task.id, schedules });
        return;
      }

      const timeslot = createTimeslotFromCalendarEvent(changeEvent);

      // A deadline marker keys its id off the deadline date, so it hits the same stale-id
      // problem. The task's own event id is `task.id` and survives the move untouched.
      if (extendedProps.isDeadline) {
        changeEvent.revert();
      }

      handleTaskUpdate(task, start, timeslot, {
        isDeadline: extendedProps.isDeadline,
      });
    },
    [
      tasks,
      handleTaskUpdate,
      lifeAreas,
      createTimeslotTask,
      goals,
      handleGoalUpdate,
      weekStartsOn,
      today,
      updateTask,
      dragExternalEvent,
    ],
  );

  const onEventReceive = useCallback(
    (info: EventReceiveArg) => {
      const { draggedEl, event } = info;
      // Capture before reverting — `revert()` detaches the EventApi, after
      // which its getters no longer return the drop position.
      const date = event.start;
      const allDay = event.allDay;
      // From the drag payload, NOT the windowed calendar set — the side panel can drag tasks the calendar doesn't render.
      const { task, occurrenceDate, originDate } =
        event.extendedProps as ExtendedProps;

      // Drop a transient event; the real one re-renders from the Firestore write.
      info.revert();

      if (!draggedEl || !date) {
        return;
      }

      const dataEl = draggedEl.querySelector('[data-component-type]');
      const el = dataEl ?? draggedEl;
      const id = el.getAttribute('data-id');

      if (!id) {
        return;
      }

      const type = el.getAttribute('data-component-type') as
        | DataType
        | undefined;
      const title = el.getAttribute('data-title'); // Used as fallback or direct name
      const rawDuration = el.getAttribute('data-duration');
      const customDurationMs = rawDuration ? parseInt(rawDuration) : null;
      // Ensure customDurationMs is valid (positive number), otherwise treat as null.
      const validCustomDurationMs = customDurationMs || null;

      // Reuse the drop-style timeslot path (date + allDay, no `oldEvent`).
      const dropArg = { date, allDay } as unknown as DropArg;

      if (type === DataType.LifeArea) {
        const lifeArea = lifeAreas.find((lifeArea) => lifeArea.id === id);
        if (!lifeArea || !title) {
          return;
        }

        // If the lifeArea is dropped outside allDay, create a new task
        createTimeslotTask(
          {
            id: lifeArea.id,
            name: title,
            actionType: TaskActionType.LifeAreaTimeslot,
          },
          date,
          createTimeslotFromCalendarEvent(dropArg, validCustomDurationMs),
        );
        return;
      }

      if (type === DataType.Goal) {
        if (!title) {
          return;
        }

        // If the goal is dropped in allDay, update its deadline
        if (allDay) {
          const goal = goals.find((goal) => goal.id === id);
          if (goal) {
            handleGoalUpdate(goal, date);
          }
          return;
        }
        createTimeslotTask(
          { id, name: title, actionType: TaskActionType.GoalTimeslot },
          date,
          createTimeslotFromCalendarEvent(dropArg, validCustomDurationMs),
        );
        return;
      }

      if (!task) {
        // Reaching here means an external-drag producer broke the payload
        // contract: every task drop must carry the full task in extendedProps.
        getErrorReporter().captureException(
          new Error('Calendar drop payload is missing extendedProps.task'),
          { tags: { source: 'calendar-event-receive' } },
        );
        return;
      }

      handleTaskUpdate(
        task,
        date,
        createTimeslotFromCalendarEvent(dropArg, validCustomDurationMs),
        { occurrenceDate, originDate },
      );
    },
    [handleTaskUpdate, lifeAreas, createTimeslotTask, goals, handleGoalUpdate],
  );

  return {
    handleTaskUpdate,
    handleGoalUpdate,
    onEventChange,
    onEventReceive,
    onSelect,
    onDateTime,
    slotContextMenu,
  };
};
